import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    clampRectToBounds,
    normalizeDragRect,
    usePixelStretch,
} from "./PixelStretchContext";
import type { PixelRect } from "../../utils/pixelStretch";
import { useUndoRedoKeyboard } from "../../hooks/useUndoRedoKeyboard";
import "./PixelStretch.scss";

const PREVIEW_ZOOM_MIN = 0.1;
const PREVIEW_ZOOM_MAX = 4;
const MIN_RECT_SIZE = 4;
const HANDLE_SIZE = 8;

type DragKind =
    | { type: "addRect"; startX: number; startY: number; curX: number; curY: number }
    | { type: "move"; nodeId: string; startX: number; startY: number; origFrame: PixelRect }
    | {
          type: "resize";
          nodeId: string;
          handle: "nw" | "ne" | "sw" | "se";
          startX: number;
          startY: number;
          origFrame: PixelRect;
      };

function clientToArtboard(
    el: HTMLElement,
    clientX: number,
    clientY: number,
    artboardW: number,
    artboardH: number,
    zoom: number,
): { x: number; y: number } | null {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const x = ((clientX - rect.left) / rect.width) * artboardW;
    const y = ((clientY - rect.top) / rect.height) * artboardH;
    return {
        x: Math.max(0, Math.min(artboardW, x)),
        y: Math.max(0, Math.min(artboardH, y)),
    };
}

function hitTestNode(
    nodes: { id: string; frame: PixelRect }[],
    x: number,
    y: number,
): string | null {
    for (let i = nodes.length - 1; i >= 0; i--) {
        const f = nodes[i].frame;
        if (
            x >= f.x &&
            x <= f.x + f.w &&
            y >= f.y &&
            y <= f.y + f.h
        ) {
            return nodes[i].id;
        }
    }
    return null;
}

function hitTestHandle(
    frame: PixelRect,
    x: number,
    y: number,
    zoom: number,
): "nw" | "ne" | "sw" | "se" | null {
    const hs = HANDLE_SIZE / zoom;
    const corners: Array<{ h: "nw" | "ne" | "sw" | "se"; cx: number; cy: number }> = [
        { h: "nw", cx: frame.x, cy: frame.y },
        { h: "ne", cx: frame.x + frame.w, cy: frame.y },
        { h: "sw", cx: frame.x, cy: frame.y + frame.h },
        { h: "se", cx: frame.x + frame.w, cy: frame.y + frame.h },
    ];
    for (const { h, cx, cy } of corners) {
        if (Math.abs(x - cx) <= hs && Math.abs(y - cy) <= hs) return h;
    }
    return null;
}

function resizeFrame(
    orig: PixelRect,
    handle: "nw" | "ne" | "sw" | "se",
    x: number,
    y: number,
): PixelRect {
    let { x: ox, y: oy, w, h } = orig;
    const x2 = ox + w;
    const y2 = oy + h;
    switch (handle) {
        case "nw":
            ox = x;
            oy = y;
            w = x2 - x;
            h = y2 - y;
            break;
        case "ne":
            oy = y;
            w = x - ox;
            h = y2 - y;
            break;
        case "sw":
            ox = x;
            w = x2 - x;
            h = y - oy;
            break;
        case "se":
            w = x - ox;
            h = y - oy;
            break;
    }
    if (w < 0) {
        ox += w;
        w = -w;
    }
    if (h < 0) {
        oy += h;
        h = -h;
    }
    return { x: ox, y: oy, w, h };
}

export function PixelStretchViewer() {
    const {
        imageDataUrl,
        sourceWidth,
        sourceHeight,
        nodes,
        selectedNodeId,
        toolMode,
        artboardWidth,
        artboardHeight,
        backgroundColor,
        showSourceImage,
        compositeCanvas,
        getDecodedImage,
        setSelectedNodeId,
        addNode,
        updateNode,
        pushNodesSnapshot,
        undo,
        redo,
    } = usePixelStretch();

    const stageRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<DragKind | null>(null);
    const [previewZoom, setPreviewZoom] = useState(1);
    const [draftRect, setDraftRect] = useState<PixelRect | null>(null);

    const artboardW = Math.max(1, artboardWidth);
    const artboardH = Math.max(1, artboardHeight);
    const aspect = artboardW / artboardH;

    const stageSize = useMemo(() => {
        const baseH = 520;
        let w = baseH * aspect;
        let h = baseH;
        if (w > 900) {
            w = 900;
            h = w / aspect;
        }
        return { width: w * previewZoom, height: h * previewZoom };
    }, [aspect, previewZoom]);

    const selectedNode = nodes.find((n) => n.id === selectedNodeId);

    useUndoRedoKeyboard({ undo, redo });

    const drawOverlay = useCallback(
        (ctx: CanvasRenderingContext2D) => {
            ctx.clearRect(0, 0, artboardW, artboardH);
            for (const node of nodes) {
                const f = node.frame;
                const sel = node.id === selectedNodeId;
                ctx.strokeStyle = sel ? "#4a9eff" : "rgba(74, 158, 255, 0.5)";
                ctx.lineWidth = sel ? 2 : 1;
                ctx.strokeRect(f.x + 0.5, f.y + 0.5, f.w - 1, f.h - 1);
                if (sel) {
                    ctx.fillStyle = "#ffffff";
                    ctx.strokeStyle = "#4a9eff";
                    const hs = HANDLE_SIZE;
                    const corners = [
                        [f.x, f.y],
                        [f.x + f.w, f.y],
                        [f.x, f.y + f.h],
                        [f.x + f.w, f.y + f.h],
                    ];
                    for (const [cx, cy] of corners) {
                        ctx.fillRect(cx - hs / 2, cy - hs / 2, hs, hs);
                        ctx.strokeRect(cx - hs / 2, cy - hs / 2, hs, hs);
                    }
                }
            }
            if (draftRect && draftRect.w > 0 && draftRect.h > 0) {
                ctx.strokeStyle = "#4a9eff";
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 4]);
                ctx.strokeRect(
                    draftRect.x,
                    draftRect.y,
                    draftRect.w,
                    draftRect.h,
                );
                ctx.setLineDash([]);
            }
        },
        [nodes, selectedNodeId, draftRect, artboardW, artboardH],
    );

    const overlayRef = useRef<HTMLCanvasElement>(null);
    useEffect(() => {
        const cv = overlayRef.current;
        if (!cv) return;
        cv.width = artboardW;
        cv.height = artboardH;
        const ctx = cv.getContext("2d");
        if (!ctx) return;
        drawOverlay(ctx);
    }, [drawOverlay, artboardW, artboardH, nodes, selectedNodeId, draftRect]);

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!imageDataUrl || !sourceWidth || !sourceHeight) return;
        const pt = clientToArtboard(
            e.currentTarget,
            e.clientX,
            e.clientY,
            artboardW,
            artboardH,
            previewZoom,
        );
        if (!pt) return;
        e.currentTarget.setPointerCapture(e.pointerId);

        if (toolMode === "addRect") {
            dragRef.current = {
                type: "addRect",
                startX: pt.x,
                startY: pt.y,
                curX: pt.x,
                curY: pt.y,
            };
            setDraftRect({ x: pt.x, y: pt.y, w: 0, h: 0 });
            return;
        }

        if (selectedNode) {
            const handle = hitTestHandle(selectedNode.frame, pt.x, pt.y, previewZoom);
            if (handle) {
                pushNodesSnapshot();
                dragRef.current = {
                    type: "resize",
                    nodeId: selectedNode.id,
                    handle,
                    startX: pt.x,
                    startY: pt.y,
                    origFrame: { ...selectedNode.frame },
                };
                return;
            }
        }

        const hitId = hitTestNode(nodes, pt.x, pt.y);
        if (hitId) {
            const node = nodes.find((n) => n.id === hitId)!;
            setSelectedNodeId(hitId);
            pushNodesSnapshot();
            dragRef.current = {
                type: "move",
                nodeId: hitId,
                startX: pt.x,
                startY: pt.y,
                origFrame: { ...node.frame },
            };
            return;
        }

        setSelectedNodeId(null);
    };

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag) return;
        const pt = clientToArtboard(
            e.currentTarget,
            e.clientX,
            e.clientY,
            artboardW,
            artboardH,
            previewZoom,
        );
        if (!pt) return;

        if (drag.type === "addRect") {
            drag.curX = pt.x;
            drag.curY = pt.y;
            const r = normalizeDragRect(drag.startX, drag.startY, drag.curX, drag.curY);
            setDraftRect(r);
            return;
        }

        if (drag.type === "move") {
            const dx = pt.x - drag.startX;
            const dy = pt.y - drag.startY;
            updateNode(drag.nodeId, {
                frame: {
                    ...drag.origFrame,
                    x: drag.origFrame.x + dx,
                    y: drag.origFrame.y + dy,
                },
            });
            return;
        }

        if (drag.type === "resize") {
            let frame = resizeFrame(drag.origFrame, drag.handle, pt.x, pt.y);
            if (frame.w < MIN_RECT_SIZE) frame = { ...frame, w: MIN_RECT_SIZE };
            if (frame.h < MIN_RECT_SIZE) frame = { ...frame, h: MIN_RECT_SIZE };
            updateNode(drag.nodeId, { frame });
        }
    };

    const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        dragRef.current = null;
        if (!drag) return;

        if (drag.type === "addRect" && sourceWidth && sourceHeight) {
            const r = normalizeDragRect(
                drag.startX,
                drag.startY,
                drag.curX,
                drag.curY,
            );
            setDraftRect(null);
            if (r.w >= MIN_RECT_SIZE && r.h >= MIN_RECT_SIZE) {
                const crop = clampRectToBounds(r, sourceWidth, sourceHeight);
                addNode(crop, { ...crop });
            }
            return;
        }

        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
            /* already released */
        }
    };

    const previewUrl = compositeCanvas?.toDataURL("image/png") ?? null;
    const sourceImg = getDecodedImage();

    return (
        <div className="panel-viewer pixel-stretch-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={() =>
                            setPreviewZoom((z) =>
                                Math.max(PREVIEW_ZOOM_MIN, z / 1.25),
                            )
                        }
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">
                        {Math.round(previewZoom * 100)}%
                    </span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={() =>
                            setPreviewZoom((z) =>
                                Math.min(PREVIEW_ZOOM_MAX, z * 1.25),
                            )
                        }
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={() => setPreviewZoom(1)}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>
                <div className="svg-scroll-wrapper">
                    {!imageDataUrl ? (
                        <div className="svg-placeholder">
                            <p>Upload a raster image to start placing crop nodes.</p>
                        </div>
                    ) : (
                        <div
                            ref={stageRef}
                            className="pixel-stretch-stage"
                            style={{
                                width: stageSize.width,
                                height: stageSize.height,
                                backgroundColor,
                                cursor:
                                    toolMode === "addRect"
                                        ? "crosshair"
                                        : "default",
                            }}
                            onPointerDown={onPointerDown}
                            onPointerMove={onPointerMove}
                            onPointerUp={onPointerUp}
                            onPointerLeave={onPointerUp}
                        >
                            {showSourceImage && sourceImg && (
                                <img
                                    className="pixel-stretch-source"
                                    src={imageDataUrl}
                                    alt=""
                                    draggable={false}
                                    style={{
                                        opacity: 0.35,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "fill",
                                    }}
                                />
                            )}
                            {previewUrl && nodes.length > 0 ? (
                                <img
                                    className="pixel-stretch-composite"
                                    src={previewUrl}
                                    alt=""
                                    draggable={false}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "fill",
                                    }}
                                />
                            ) : null}
                            <canvas
                                ref={overlayRef}
                                className="pixel-stretch-overlay"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
